import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';
import letrusBrandUrl from 'assets/images/logoLetrus.svg';
import Menu from 'components/Menu';
import { PageWrapper } from 'components/ui';
import getFeatureFlag from 'utils/functions/getFeatureFlags';
import { getUserData } from 'utils/functions/getUserData';
import { useMobile } from 'utils/hooks';
import useTrackEvent from 'utils/hooks/useTrackEvent';

interface OwnProps {
  children: React.ReactNode | React.ReactNode[];
}

function AuthLayout({ children }: OwnProps) {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { pathname } = useLocation();
  const { trackEvent } = useTrackEvent();

  const isEvolutionOn = getFeatureFlag('evolution_portal').isOn;
  const shouldShowLibraryOnMenu = getFeatureFlag('teacher_library').isOn;
  const shouldShowC5OnMenu = getFeatureFlag('teacher_c5_dashboard').isOn;
  const enableNewHomeFlag = getFeatureFlag('enable_new_home').isOn;
  const localUserData = getUserData();

  const shouldShowSocioEmotionalOnMenu = getFeatureFlag(
    'teacher_socioemotional_dashboard'
  ).isOn;

  const shouldShowRecommendationsOnMenu = getFeatureFlag(
    'teacher_recommendations_alerts'
  ).isOn;

  const shouldShowEngagementProfileOnMenu =
    getFeatureFlag('teacher_engagement').isOn;

  const topSidebarItems = [
    mountNewSidebarItems(
      'Página Inicial',
      { icon: ['fad', 'house-chimney'] },
      pathname === '/',
      () => navigate('/')
    ),
    mountNewSidebarItems(
      'Trilhas',
      { icon: ['fad', 'train-track'] },
      enableNewHomeFlag
        ? pathname === '/v2/atividades'
        : pathname === '/atividades/regulares',
      onClickLearningPath
    ),
    mountNewSidebarItems(
      'Evolução',
      { icon: ['fad', 'chart-line'] },
      pathname === '/evolucao',
      onClickEvolution
    ),
    ...(shouldShowRecommendationsOnMenu
      ? [
          mountNewSidebarItems(
            'Ações',
            { icon: ['fad', 'lightbulb-on'] },
            pathname === '/recomendacoes',
            () => navigate('/recomendacoes')
          ),
        ]
      : []),
    ...(shouldShowEngagementProfileOnMenu
      ? [
          mountNewSidebarItems(
            'Perfis',
            { icon: ['fad', 'address-card'] },
            pathname === '/perfil-estudantes',
            onClickEngagementProfile
          ),
        ]
      : []),

    mountNewSidebarItems(
      'Turmas',
      { icon: ['fad', 'graduation-cap'] },
      pathname === '/turmas',
      () => navigate(`/turmas`)
    ),
    mountNewSidebarItems(
      'Temas',
      { icon: ['fad', 'clipboard-list'] },
      pathname === `/temas`,
      () => navigate(`/temas`)
    ),
    mountNewSidebarItems(
      'Recursos Formativos',
      { icon: ['fad', 'clipboard-check'] },
      pathname === '/recursos-formativos',
      () => navigate(`/recursos-formativos`)
    ),
    ...(shouldShowLibraryOnMenu
      ? [
          mountNewSidebarItems(
            'Biblioteca',
            { icon: ['fad', 'book-open'] },
            pathname === '/biblioteca',
            () => navigate(`/biblioteca`)
          ),
        ]
      : []),
    ...(shouldShowSocioEmotionalOnMenu
      ? [
          mountNewSidebarItems(
            'Emocional',
            { icon: ['fad', 'face-smile-beam'] },
            pathname === '/socio-emocional',
            () => navigate(`/socio-emocional`)
          ),
        ]
      : []),
    mountNewSidebarItems(
      'FAQ',
      { icon: ['fad', 'question-circle'] },
      pathname === '/centraldeconhecimento',
      () => navigate(`/centraldeconhecimento`)
    ),
    ...(shouldShowC5OnMenu
      ? [
          mountNewSidebarItems(
            'C5',
            { icon: ['fad', 'bullseye-arrow'] },
            pathname === '/c5',
            () => navigate('/c5')
          ),
        ]
      : []),
  ];

  function mountNewSidebarItems(
    label: string,
    icon: FontAwesomeIconProps,
    active: boolean,
    onClick: () => void,
    notificationCount?: number
  ) {
    return {
      label,
      icon,
      active,
      onClick,
      notificationCount: notificationCount || undefined,
    };
  }

  function onClickLearningPath() {
    trackEvent('sidebar_click_learning_path');
    navigate(enableNewHomeFlag ? '/v2/atividades' : '/atividades/regulares');
  }

  function onClickEvolution() {
    return isEvolutionOn
      ? navigate(`/evolucao/dashboard`)
      : navigate(`/evolucao`);
  }

  function onClickEngagementProfile() {
    navigate('/perfil-estudantes');

    trackEvent('accessed_profiles_page_teacher', {
      user: {
        userrole_id: localUserData?.userRole?.id,
      },
      school: {
        school_id: localUserData?.userRole?.school,
        schoolgroup_id: localUserData?.userRole?.school_group.id,
      },
    });
  }

  return (
    <PageWrapper
      topMenu={topSidebarItems}
      isMenuButtonDisabled={!isMobile}
      theme="dark"
      centerComponent={
        <>
          {localUserData?.userRole?.network_logo && (
            <img
              src={localUserData?.userRole?.network_logo}
              alt="logo da rede"
            />
          )}
          <img src={letrusBrandUrl} alt="Letrus" />
        </>
      }
      rightComponent={<Menu />}
      userRole="teacher"
    >
      {children}
    </PageWrapper>
  );
}

export default AuthLayout;
